/* sugar:assets/upsells.js@2.2.0 */
/*
 * upsells — one engine for Product Upsells and Cart Upsells.
 *
 * A root is any [data-sugar-upsells]; its data-upsells-config says which mode it runs in:
 *   queue — the page block. Selected rows are handed to whichever buy button submits, through
 *           SugarCart.extras (the root carries data-cart-extras and exposes sugarExtras), so
 *           nothing here intercepts the form.
 *   cart  — the drawer block. A tap adds or removes the line straight away.
 *
 * Rows are Upsell Item children and collection products rendered by Liquid, plus fetched
 * recommendations cloned from the root's <template>. Prices are painted here from each row's
 * data attributes, which is what applies the parent's discount, badge and currency format to
 * rows that can't know their host in Liquid.
 *
 * With "Add to the buy button price" the root carries data-upsells-total (the added rows' shown
 * prices, in cents) and every change fires upsells:changed { sectionId, total } on document, which
 * product-buy-button.js adds to its [price]. "Learn more" opens one shared side panel filled
 * from the row's product (/products/<handle>.js).
 */
(function () {
  'use strict';
  if (window.SugarUpsells) return;

  const ROOT = '[data-sugar-upsells]';
  const ROW = '[data-upsell-row]';
  const ATTRIBUTION_PROP = '_sugar_upsell';
  const CACHE_MS = 120000;

  const int = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const num = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  // trim: a whole amount drops its .00 (the cart drawer's "Hide .00 on whole amounts").
  function formatMoney(cents, format, trim) {
    const whole = !!trim && Math.round(cents) % 100 === 0;
    if (window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
      return window.SugarPricing.formatMoney(cents, format, whole);
    }
    return (format || '${{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, (cents / 100).toFixed(whole ? 0 : 2));
  }

  function discounted(price, type, value) {
    if (type === 'percentage' && value > 0) return Math.round(price * (1 - value / 100));
    if (type === 'fixed' && value > 0) return Math.max(0, price - Math.round(value * 100));
    return price;
  }

  function drawerEl() {
    return document.querySelector('sugar-cart-drawer');
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    const drawer = drawerEl();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((section) => section.id);
    }
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  async function postAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    return res.json();
  }

  async function announce(result, source, paintDrawer) {
    const drawer = drawerEl();
    const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
    if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData, sections: result.sections });
    }
    if (paintDrawer) {
      if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(result);
      else if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.reload();
    }
    document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
  }

  function make(tag, className) {
    const el = document.createElement(tag);
    el.className = className;
    return el;
  }

  /* ------------------------------------------------------------ learn more panel */

  // One side panel serves every row: the row's product, fetched once, with its pictures in a
  // scroller under a "1 / N" pill between two arrows, its title, prices and description.
  const productCache = new Map();
  let panel = null;
  let panelReturn = null;

  function fetchProduct(handle) {
    if (!productCache.has(handle)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productCache.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    return productCache.get(handle);
  }

  function panelCount() {
    const track = panel.querySelector('.sugar-product-upsells__learn-track');
    const slides = track.children.length;
    const index = slides ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + 1 : 0;
    const count = panel.querySelector('.sugar-product-upsells__learn-count');
    const text = slides ? index + ' / ' + slides : '';
    if (count.textContent !== text) count.textContent = text;
    panel.querySelector('.sugar-product-upsells__learn-nav').hidden = slides < 2;
  }

  function closeLearnMore() {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    document.documentElement.classList.remove('sugar-product-upsells--learn-lock');
    setTimeout(() => { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 300);
    if (panelReturn && panelReturn.focus) panelReturn.focus();
  }

  function buildPanel() {
    panel = document.createElement('div');
    panel.className = 'sugar-product-upsells__learn';
    panel.hidden = true;
    panel.innerHTML =
      '<div class="sugar-product-upsells__learn-backdrop" data-learn-close></div>' +
      '<div class="sugar-product-upsells__learn-panel" role="dialog" aria-modal="true" aria-labelledby="SugarUpsellLearnTitle" tabindex="-1">' +
      '<button type="button" class="sugar-product-upsells__learn-close" data-learn-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<div class="sugar-product-upsells__learn-media"><div class="sugar-product-upsells__learn-track"></div>' +
      '<div class="sugar-product-upsells__learn-nav"><button type="button" data-learn-step="-1" aria-label="Previous picture"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6"/></svg></button>' +
      '<span class="sugar-product-upsells__learn-count"></span>' +
      '<button type="button" data-learn-step="1" aria-label="Next picture"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button></div></div>' +
      '<div class="sugar-product-upsells__learn-body"><h2 class="sugar-product-upsells__learn-title" id="SugarUpsellLearnTitle"></h2>' +
      '<div class="sugar-product-upsells__learn-prices"></div><div class="sugar-product-upsells__learn-description rte"></div></div></div>';
    document.body.appendChild(panel);
    panel.addEventListener('click', (e) => {
      if (e.target.closest('[data-learn-close]')) {
        closeLearnMore();
        return;
      }
      const step = e.target.closest('[data-learn-step]');
      if (step) {
        const track = panel.querySelector('.sugar-product-upsells__learn-track');
        track.scrollBy({ left: track.clientWidth * parseInt(step.dataset.learnStep, 10), behavior: 'smooth' });
      }
    });
    panel.querySelector('.sugar-product-upsells__learn-track').addEventListener('scroll', panelCount, { passive: true });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeLearnMore();
    });
  }

  async function openLearnMore(row, host) {
    const handle = row.dataset.handle;
    if (!handle) return;
    if (!panel) buildPanel();
    panelReturn = row.querySelector('[data-upsell-learn]');
    const box = panel.querySelector('.sugar-product-upsells__learn-panel');
    // The host's colours and type travel with the panel, which lives on <body>
    const style = getComputedStyle(host.root);
    box.style.setProperty('--upl-bg', style.getPropertyValue('--up-item-bg') || '#fff');
    box.style.setProperty('--upl-text', style.getPropertyValue('--up-item-text') || 'inherit');
    const title = panel.querySelector('.sugar-product-upsells__learn-title');
    const track = panel.querySelector('.sugar-product-upsells__learn-track');
    const prices = panel.querySelector('.sugar-product-upsells__learn-prices');
    const description = panel.querySelector('.sugar-product-upsells__learn-description');
    title.textContent = (row.querySelector('.sugar-upsell-item__title') || {}).textContent || '';
    prices.innerHTML = '';
    const rowPrices = row.querySelector('.sugar-upsell-item__prices');
    if (rowPrices) {
      ['.sugar-upsell-item__compare', '.sugar-upsell-item__price'].forEach((sel) => {
        const el = rowPrices.querySelector(sel);
        if (el) prices.appendChild(el.cloneNode(true));
      });
    }
    track.innerHTML = '';
    description.innerHTML = '';
    panel.hidden = false;
    document.documentElement.classList.add('sugar-product-upsells--learn-lock');
    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      box.focus({ preventScroll: true });
    });
    const product = await fetchProduct(handle);
    if (!product || panel.hidden) return;
    (product.media && product.media.length ? product.media.filter((m) => m.media_type === 'image').map((m) => m.src) : product.images || []).forEach((src, i) => {
      const img = document.createElement('img');
      img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=900';
      img.alt = product.title || '';
      img.loading = i === 0 ? 'eager' : 'lazy';
      img.width = 900;
      img.height = 900;
      track.appendChild(img);
    });
    track.scrollLeft = 0;
    panelCount();
    description.innerHTML = product.description || '';
  }

  class Upsells {
    constructor(root) {
      this.root = root;
      root.sugarUpsells = this;
      try {
        this.cfg = JSON.parse(root.dataset.upsellsConfig || '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.cart = this.cfg.mode === 'cart';
      this.list = root.querySelector('[data-upsell-list]');
      this.template = root.querySelector('[data-upsell-template]');
      this.iconSource = root.querySelector('[data-upsell-added-icon-source]');
      this.selected = new Set();
      this.handlers = [];
      this.destroyed = false;
      this.busy = false;
      this.splide = null;
      this.chrome = null;
      this.splideTimer = null;
      this.splideTries = 0;
      this.recCache = new Map();
      this.recRequest = 0;
      this.recSignature = '';
      this.cartData = null;
      this.init();
    }

    /* ------------------------------------------------------------ setup */

    init() {
      if (!this.list) return;
      this.tidyList();
      this.rows().forEach((row) => this.decorate(row));
      this.bindEvents();
      if (this.cart) this.initCart();
      else this.initQueue();
      if (this.cfg.recommendations && !this.cart) this.fetchPageRecommendations();
      if (this.cfg.layout === 'slider') this.mountSlider();
      this.setupAnalytics();
    }

    destroy() {
      this.destroyed = true;
      this.unmountSlider();
      clearTimeout(this.refreshTimer);
      this.handlers.forEach(([target, type, fn, opts]) => target.removeEventListener(type, fn, opts));
      this.handlers = [];
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      if (this.drawerWatcher) this.drawerWatcher.disconnect();
      if (this.vwObserver) this.vwObserver.disconnect();
      delete this.root.sugarUpsells;
      delete this.root.sugarExtras;
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.handlers.push([target, type, fn, opts]);
    }

    rows() {
      return this.list ? Array.from(this.list.querySelectorAll(ROW)) : [];
    }

    // An Upsell Item renders with no wrapper, so its own style and script tags land in the
    // list beside the row. Anything that is not a row moves out before the list is measured
    // or a slider counts its children as slides.
    tidyList() {
      Array.from(this.list.children).forEach((node) => {
        if (node.matches(ROW) || node.querySelector(ROW)) return;
        this.root.appendChild(node);
      });
    }

    key(row) {
      return this.cart ? String(row.dataset.productId) : String(row.dataset.rowKey);
    }

    isSelected(row) {
      return this.selected.has(this.key(row));
    }

    decorate(row) {
      if (row.dataset.decorated) return;
      row.dataset.decorated = '1';
      if (this.cfg.control === 'button') {
        row.removeAttribute('tabindex');
      } else {
        row.setAttribute('role', 'checkbox');
      }
      if (this.iconSource) {
        row.querySelectorAll('[data-upsell-added-icon]').forEach((slot) => {
          slot.innerHTML = '';
          slot.appendChild(this.iconSource.content.cloneNode(true));
        });
      }
      this.paintPrices(row);
      this.paintRow(row);
    }

    bindEvents() {
      this.listen(this.root, 'click', (e) => {
        const row = e.target.closest(ROW);
        if (!row || !this.list.contains(row)) return;
        if (e.target.closest('[data-upsell-learn]')) {
          e.preventDefault();
          openLearnMore(row, this);
          return;
        }
        if (row.dataset.available === 'false') return;
        if (e.target.closest('select, a')) return;
        if (e.target.closest('.sugar-upsell-item__button')) {
          e.preventDefault();
          this.onButton(row);
          return;
        }
        if (this.cfg.control === 'button') return;
        this.onRowTap(row);
      });
      this.listen(this.root, 'change', (e) => {
        const select = e.target.closest('.sugar-upsell-item__select');
        if (!select) return;
        const row = select.closest(ROW);
        if (row) this.onVariantChange(row, select);
      });
      this.listen(this.root, 'keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        const row = e.target && e.target.closest ? e.target.closest(ROW) : null;
        if (!row || e.target !== row || this.cfg.control === 'button') return;
        if (row.dataset.available === 'false') return;
        e.preventDefault();
        this.onRowTap(row);
      });
    }

    /* ------------------------------------------------------------ prices */

    paintPrices(row) {
      const price = int(row.dataset.price);
      const prices = row.querySelector('.sugar-upsell-item__prices');
      if (!price || !prices) return;
      const compare = int(row.dataset.compare);
      let type = row.dataset.discountType || 'none';
      let value = num(row.dataset.discountValue);
      if (type === 'none' || value <= 0) {
        type = this.cfg.discountType || 'none';
        value = num(this.cfg.discountValue);
      }
      const shown = discounted(price, type, value);
      const hasCompare = compare > price;
      const compareShown = hasCompare ? compare : price;
      const format = this.cfg.moneyFormat;
      const trim = this.cfg.hideCents;

      row.dataset.shown = String(shown);
      row.dataset.compareShown = String(compareShown);
      const priceEl = prices.querySelector('.sugar-upsell-item__price');
      if (priceEl) priceEl.textContent = formatMoney(shown, format, trim);

      let compareEl = prices.querySelector('.sugar-upsell-item__compare');
      let badgeEl = prices.querySelector('.sugar-upsell-item__badge');
      if (shown < price || hasCompare) {
        if (!compareEl) {
          compareEl = make('span', 'sugar-upsell-item__compare');
          prices.appendChild(compareEl);
        }
        compareEl.textContent = formatMoney(compareShown, format, trim);
        if (!badgeEl) {
          badgeEl = make('span', 'sugar-upsell-item__badge');
          prices.appendChild(badgeEl);
        }
        const saved = compareShown - shown;
        const percentage = compareShown > 0 ? Math.round((saved / compareShown) * 100) : 0;
        const text = this.cfg.badgeText || '';
        badgeEl.innerHTML = text
          ? text
              .replace(/\[percentage\]/g, String(percentage))
              .replace(/\[amount_saved\]/g, formatMoney(saved, format, trim))
              .replace(/\[price\]/g, formatMoney(shown, format, trim))
              .replace(/\[compare_price\]/g, formatMoney(compareShown, format, trim))
          : '';
      } else {
        if (compareEl) compareEl.remove();
        if (badgeEl) badgeEl.remove();
      }
    }

    onVariantChange(row, select) {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      row.dataset.variantId = option.value;
      if (option.dataset.price) row.dataset.price = option.dataset.price;
      row.dataset.compare = option.dataset.compare || '0';
      if (option.dataset.image) {
        const img = row.querySelector('.sugar-upsell-item__media img');
        if (img) img.src = option.dataset.image;
      }
      const swatch = row.querySelector('.sugar-upsell-item__swatch');
      if (swatch) {
        if (option.dataset.swatch) swatch.style.setProperty('--up-swatch', option.dataset.swatch);
        else swatch.removeAttribute('style');
      }
      this.paintPrices(row);
      if (this.isSelected(row)) this.publishTotal();
    }

    // The added rows' shown prices, for a buy button that counts them in its [price]
    publishTotal() {
      if (!this.cfg.addToPrice || this.cart) return;
      let total = 0;
      const seen = new Set();
      this.rows().forEach((row) => {
        const key = this.key(row);
        if (!this.selected.has(key) || seen.has(key)) return;
        seen.add(key);
        total += int(row.dataset.shown, int(row.dataset.price)) * Math.max(1, int(row.dataset.quantity, 1));
      });
      const value = String(total);
      if (this.root.dataset.upsellsTotal === value) return;
      this.root.dataset.upsellsTotal = value;
      const section = this.root.closest('.shopify-section');
      document.dispatchEvent(new CustomEvent('upsells:changed', {
        detail: { sectionId: section ? section.id.replace(/^shopify-section-/, '') : null, total }
      }));
    }

    /* ------------------------------------------------------------ state */

    paintRow(row) {
      const on = this.isSelected(row);
      row.classList.toggle('is-selected', on);
      if (row.getAttribute('role') === 'checkbox') row.setAttribute('aria-checked', on ? 'true' : 'false');
      const text = row.querySelector('[data-upsell-button-text]');
      if (text) {
        const showAdded = on && (this.cart ? !!this.cfg.addOnce : !this.cfg.independentAdd);
        text.innerHTML = showAdded ? this.cfg.addedText || 'Added' : this.cfg.addText || 'Add';
      }
      const button = row.querySelector('.sugar-upsell-item__button');
      if (button) button.classList.toggle('is-inert', on && this.cart && !!this.cfg.addOnce);
    }

    setSelected(row, on, opts = {}) {
      if (on) this.selected.add(this.key(row));
      else this.selected.delete(this.key(row));
      this.paintRow(row);
      if (on && !opts.silent) this.track('select', row.dataset.variantId);
      this.publishTotal();
    }

    onRowTap(row) {
      if (this.cart) {
        if (this.isSelected(row)) this.removeCartRow(row);
        else this.addCartRow(row);
        return;
      }
      this.setSelected(row, !this.isSelected(row));
    }

    onButton(row) {
      if (this.cart) {
        if (this.cfg.addOnce && this.isSelected(row)) return;
        this.addCartRow(row);
        return;
      }
      if (this.cfg.independentAdd) {
        this.addNow(row);
        return;
      }
      this.setSelected(row, !this.isSelected(row));
    }

    lineFor(row) {
      const id = int(row.dataset.variantId);
      if (!id) return null;
      const line = { id, quantity: Math.max(1, int(row.dataset.quantity, 1)) };
      const attribution = this.attribution();
      if (attribution) line.properties = { [ATTRIBUTION_PROP]: attribution };
      return line;
    }

    /* ------------------------------------------------------------ queue mode (page) */

    initQueue() {
      this.root.sugarExtras = {
        collect: () => this.collectLines(),
        commit: () => this.commitLines(),
      };
      this.rows().forEach((row) => {
        if (row.dataset.preselected === 'true' && row.dataset.available !== 'false') {
          this.setSelected(row, true, { silent: true });
        }
      });
    }

    collectLines() {
      const lines = [];
      this.rows().forEach((row) => {
        if (!this.isSelected(row)) return;
        const line = this.lineFor(row);
        if (line) lines.push(line);
      });
      return lines;
    }

    commitLines() {
      this.rows().forEach((row) => {
        if (this.isSelected(row)) this.track('atc', row.dataset.variantId);
      });
      this.selected.clear();
      this.rows().forEach((row) => this.paintRow(row));
      this.publishTotal();
    }

    async addNow(row) {
      if (this.busy) return;
      this.busy = true;
      try {
        const line = this.lineFor(row);
        if (!line) return;
        const result = await postAdd([line]);
        if (result.status) {
          console.error('[Upsells] Cart error:', result.description);
          return;
        }
        // The click is both the intent and the add, so the funnel stays monotonic.
        this.track('select', line.id);
        this.track('atc', line.id);
        await announce(result, 'product-upsells', true);
      } catch (e) {
        console.error('[Upsells] Add error:', e);
      } finally {
        this.busy = false;
      }
    }

    /* ------------------------------------------------------------ cart mode (drawer) */

    initCart() {
      this.listen(document, 'cart:update', () => this.queueRefresh({ forceCart: true }));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const hasCart = !!(event && event.cartData && Array.isArray(event.cartData.items));
          if (hasCart) this.cartData = event.cartData;
          this.queueRefresh({ forceCart: !hasCart });
        });
      }
      this.queueRefresh({ forceCart: true, forceRecommendations: true });
    }

    queueRefresh(opts) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refresh(opts).catch((e) => console.error('[Upsells] Refresh error:', e));
      }, 40);
    }

    async refresh({ forceCart = false, forceRecommendations = false } = {}) {
      const cart = await this.fetchCart(forceCart);
      if (!cart || this.destroyed) return;
      this.cartData = cart;
      this.applyVisibility(cart);
      this.syncWithCart(cart);
      if (this.cfg.recommendations) await this.fetchCartRecommendations(cart, forceRecommendations);
      this.root.classList.remove('sugar-upsells--pending');
    }

    async fetchCart(force) {
      if (!force && this.cartData && Array.isArray(this.cartData.items)) return this.cartData;
      if (window.SugarCart) return window.SugarCart.get({ fresh: !!force });
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    }

    applyVisibility(cart) {
      const rules = this.cfg.visibility;
      if (!rules) return;
      const products = Array.isArray(rules.products) ? rules.products : [];
      const groups = Array.isArray(rules.collections) ? rules.collections : [];
      let visible = true;
      if (products.length || groups.length) {
        const inCart = new Set(cart.items.map((item) => item.product_id));
        const checks = [];
        products.forEach((id) => checks.push(inCart.has(id)));
        groups.forEach((ids) => checks.push(ids.some((id) => inCart.has(id))));
        visible = rules.match === 'all' ? checks.every(Boolean) : checks.some(Boolean);
      }
      this.visible = visible;
      this.root.classList.toggle('sugar-upsells--hidden', !visible);
    }

    isTagged(item) {
      const p = item && item.properties;
      return !!(p && (p._cart_upsell === 'true' || p._cart_toggle === 'true'));
    }

    syncWithCart(cart) {
      if (!Array.isArray(cart.items)) return;
      const inCart = new Set(cart.items.map((item) => item.product_id));
      const tagged = new Set(cart.items.filter((item) => this.isTagged(item)).map((item) => item.product_id));
      const next = new Set();
      this.rows().forEach((row) => {
        const pid = int(row.dataset.productId);
        if (!pid) return;
        // Hide-when-in-cart counts any line holding the product, whoever added it; otherwise a
        // toggle only owns the line it tagged, while a button matches any line.
        const added = this.cfg.hideWhenInCart || this.cfg.control === 'button' ? inCart.has(pid) : tagged.has(pid);
        if (added) next.add(String(pid));
      });
      this.selected = next;
      this.rows().forEach((row) => this.paintRow(row));
      if (this.splide && this.cfg.hideWhenInCart) this.mountSlider();
    }

    async addCartRow(row) {
      if (this.busy) return;
      this.busy = true;
      const key = this.key(row);
      this.selected.add(key);
      this.paintRow(row);
      try {
        const line = this.lineFor(row);
        if (!line) return;
        if (this.cfg.control !== 'button') {
          line.properties = Object.assign({ _cart_upsell: 'true' }, line.properties || {});
        }
        const result = await postAdd([line]);
        if (result.status) {
          console.error('[Upsells] Cart error:', result.description);
          this.selected.delete(key);
          this.paintRow(row);
          return;
        }
        this.track('select', line.id);
        this.track('atc', line.id);
        await announce(result, 'cart-upsells', false);
      } catch (e) {
        console.error('[Upsells] Add error:', e);
        this.selected.delete(key);
        this.paintRow(row);
      } finally {
        this.busy = false;
      }
    }

    async removeCartRow(row) {
      if (this.busy) return;
      this.busy = true;
      const key = this.key(row);
      this.selected.delete(key);
      this.paintRow(row);
      try {
        const variantId = int(row.dataset.variantId);
        let removeId = String(variantId);
        if (this.cfg.control !== 'button') {
          // Remove the line this block tagged, never a line the shopper added themselves.
          const cart = await this.fetchCart(true);
          if (!cart || !Array.isArray(cart.items)) return;
          const line = cart.items.find((item) => parseInt(item.variant_id, 10) === variantId && this.isTagged(item));
          if (!line) return;
          removeId = line.key;
        }
        const payload = { id: removeId, quantity: 0, sections: sectionIds(), sections_url: window.location.pathname };
        const result = window.SugarCart && window.SugarCart.change
          ? await window.SugarCart.change(payload)
          : await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload),
            }).then((res) => res.json());
        if (result.status) {
          console.error('[Upsells] Cart error:', result.description);
          this.selected.add(key);
          this.paintRow(row);
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsells', cartData: result, sections: result.sections });
        }
        document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
      } catch (e) {
        console.error('[Upsells] Remove error:', e);
        this.selected.add(key);
        this.paintRow(row);
      } finally {
        this.busy = false;
      }
    }

    /* ------------------------------------------------------------ fetched products */

    fixedRows() {
      return this.rows().filter((row) => row.dataset.source !== 'recommendation');
    }

    fetchRoom() {
      const collection = this.rows().filter((row) => row.dataset.source === 'collection').length;
      return Math.max(0, int(this.cfg.maxFetched, 4) - collection);
    }

    async getRecommendations(productId, limit) {
      const cacheKey = productId + ':' + limit;
      const cached = this.recCache.get(cacheKey);
      const now = Date.now();
      if (cached && cached.pending) return cached.pending;
      if (cached && now - cached.at < CACHE_MS) return cached.products;
      const pending = (async () => {
        try {
          const res = await fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + limit + '&intent=related');
          if (!res.ok) throw new Error(String(res.status));
          const data = await res.json();
          const products = Array.isArray(data.products) ? data.products : [];
          this.recCache.set(cacheKey, { at: Date.now(), products });
          return products;
        } catch (e) {
          this.recCache.delete(cacheKey);
          return [];
        }
      })();
      this.recCache.set(cacheKey, { at: now, products: [], pending });
      return pending;
    }

    async fetchPageRecommendations() {
      const productId = int(this.cfg.productId);
      const room = this.fetchRoom();
      if (!productId || room <= 0) return;
      this.setLoading(true);
      try {
        const products = await this.getRecommendations(productId, int(this.cfg.maxFetched, 4));
        if (this.destroyed) return;
        const exclude = new Set(this.fixedRows().map((row) => int(row.dataset.productId)));
        exclude.add(productId);
        const picked = products
          .filter((p) => p && p.available && Array.isArray(p.variants) && p.variants.length && !exclude.has(p.id))
          .slice(0, room);
        this.renderFetched(picked);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchCartRecommendations(cart, force) {
      const requestId = ++this.recRequest;
      const max = int(this.cfg.maxFetched, 4);
      const room = this.fetchRoom();
      if (!Array.isArray(cart.items) || !cart.items.length || room <= 0) {
        this.renderFetched([]);
        this.recSignature = 'empty';
        return;
      }
      const sourceItems = cart.items.filter((item) => !this.isTagged(item));
      const sourceIds = [...new Set(sourceItems.map((item) => item.product_id))].slice(0, Math.max(1, Math.min(max, 6)));
      const keep = new Set(this.cfg.control === 'button' && !this.cfg.hideWhenInCart ? [...this.selected].map(Number) : []);
      const fixed = new Set(this.fixedRows().map((row) => int(row.dataset.productId)));
      const seen = new Set(fixed);
      sourceItems.forEach((item) => {
        if (!keep.has(item.product_id)) seen.add(item.product_id);
      });
      if (this.cfg.hideWhenInCart) cart.items.forEach((item) => seen.add(item.product_id));

      const signature = [
        max,
        sourceIds.join(','),
        this.cfg.control,
        [...keep].sort((a, b) => a - b).join(','),
        cart.items.map((item) => item.key).sort().join(','),
      ].join('|');
      if (!force && signature === this.recSignature) return;
      this.recSignature = signature;

      this.setLoading(true);
      try {
        const lists = await Promise.all(sourceIds.map((id) => this.getRecommendations(id, max)));
        if (requestId !== this.recRequest || this.destroyed) return;

        const recs = [];
        for (const products of lists) {
          for (const p of products) {
            if (recs.length >= room) break;
            if (!p || !p.available || !Array.isArray(p.variants) || !p.variants.length || seen.has(p.id)) continue;
            seen.add(p.id);
            recs.push(p);
          }
          if (recs.length >= room) break;
        }

        // An upsell this block added stays on the list so the shopper can review or remove it.
        const existing = new Map(
          this.rows()
            .filter((row) => row.dataset.source === 'recommendation' && row._product)
            .map((row) => [int(row.dataset.productId), row._product])
        );
        const pinned = [];
        const pinnedIds = new Set();
        if (!this.cfg.hideWhenInCart) {
          for (const item of cart.items) {
            const pid = item.product_id;
            if (pinnedIds.has(pid) || fixed.has(pid)) continue;
            const mine = (item.properties && item.properties._cart_upsell === 'true') || (this.cfg.control === 'button' && this.selected.has(String(pid)));
            if (!mine) continue;
            const product = recs.find((p) => p.id === pid) || existing.get(pid) || this.productFromLine(item);
            if (!product) continue;
            pinned.push(product);
            pinnedIds.add(pid);
          }
        }
        const rest = recs.filter((p) => !pinnedIds.has(p.id));
        this.renderFetched([...pinned, ...rest].slice(0, Math.max(room, pinned.length)));
        this.syncWithCart(cart);
      } catch (e) {
        console.error('[Upsells] Recommendations error:', e);
        this.renderFetched([]);
      } finally {
        if (requestId === this.recRequest) this.setLoading(false);
      }
    }

    productFromLine(item) {
      const productId = parseInt(item && item.product_id, 10);
      const variantId = parseInt(item && item.variant_id, 10);
      if (!Number.isFinite(productId) || !Number.isFinite(variantId)) return null;
      const image = typeof item.featured_image === 'string'
        ? item.featured_image
        : (item.featured_image && (item.featured_image.url || item.featured_image.src)) || '';
      const price = parseInt(item.final_price || item.price, 10) || 0;
      const original = parseInt(item.original_price, 10) || 0;
      return {
        id: productId,
        title: item.product_title || item.title || '',
        available: true,
        featured_image: image,
        variants: [{ id: variantId, title: item.variant_title || '', available: true, price, compare_at_price: original > price ? original : null }],
      };
    }

    renderFetched(products) {
      if (!this.template) return;
      this.tidyList();
      this.list.querySelectorAll('[data-source="recommendation"]').forEach((node) => node.remove());
      products.forEach((product) => {
        const row = this.buildRow(product);
        if (!row) return;
        this.list.appendChild(row);
        this.decorate(row);
      });
      const empty = this.root.querySelector('.sugar-upsells__empty');
      if (!this.rows().length && this.cfg.recommendations) {
        if (!empty) {
          const note = make('div', 'sugar-upsells__empty');
          note.textContent = 'No recommendations available';
          this.root.appendChild(note);
        }
      } else if (empty) {
        empty.remove();
      }
      if (this.cfg.layout === 'slider') this.mountSlider();
    }

    buildRow(product) {
      const fragment = this.template.content.cloneNode(true);
      const row = fragment.querySelector(ROW);
      if (!row) return null;
      const variants = product.variants || [];
      const variant = variants.find((v) => v.available) || variants[0];
      if (!variant) return null;
      row._product = product;
      row.dataset.rowKey = String(product.id);
      row.dataset.productId = String(product.id);
      row.dataset.variantId = String(variant.id);
      row.dataset.price = String(variant.price || 0);
      row.dataset.compare = String(variant.compare_at_price || 0);
      row.dataset.available = 'true';
      row.dataset.source = 'recommendation';
      if (product.handle) row.dataset.handle = product.handle;
      if (this.cfg.learnMore) {
        const prices = row.querySelector('.sugar-upsell-item__prices');
        const learn = make('button', 'sugar-upsell-item__learn');
        learn.type = 'button';
        learn.setAttribute('data-upsell-learn', '');
        learn.innerHTML = this.cfg.learnMore;
        if (prices) prices.after(learn);
      }

      const title = row.querySelector('.sugar-upsell-item__title');
      if (title) title.textContent = product.title || '';
      const img = row.querySelector('.sugar-upsell-item__media img');
      const src = product.featured_image || '';
      if (img && src) {
        img.src = src.indexOf('?') === -1 ? src + '?width=300' : src + '&width=300';
        img.alt = product.title || '';
      } else if (img) {
        img.remove();
      }
      const select = row.querySelector('.sugar-upsell-item__select');
      if (select) {
        const options = variants.filter((v) => v.available);
        if (this.cfg.picker && variants.length > 1 && options.length) {
          select.removeAttribute('hidden');
          options.forEach((v) => {
            const option = document.createElement('option');
            option.value = String(v.id);
            option.textContent = v.title || '';
            option.dataset.price = String(v.price || 0);
            option.dataset.compare = String(v.compare_at_price || 0);
            const image = v.featured_image && (v.featured_image.src || v.featured_image);
            if (typeof image === 'string' && image) option.dataset.image = image;
            if (v.id === variant.id) option.selected = true;
            select.appendChild(option);
          });
        } else {
          select.remove();
        }
      }
      return row;
    }

    setLoading(on) {
      let node = this.root.querySelector('.sugar-upsells__loading');
      if (on && !node) {
        node = make('div', 'sugar-upsells__loading');
        node.appendChild(make('div', 'sugar-upsells__spinner'));
        this.root.appendChild(node);
      } else if (!on && node) {
        node.remove();
      }
    }

    /* ------------------------------------------------------------ slider */

    mountSlider() {
      const splideEl = this.root.querySelector('[data-upsell-splide]');
      if (!splideEl || !this.list) return;
      const attempt = () => {
        if (this.destroyed) return;
        if (typeof Splide === 'undefined') {
          this.splideTries += 1;
          if (this.splideTries > 100) return;
          this.splideTimer = setTimeout(attempt, 100);
          return;
        }
        this.unmountSlider();
        this.tidyList();
        if (this.cart && this.cfg.hideWhenInCart) this.applyInCartVisibility();
        Array.from(this.list.children).forEach((node) => node.classList.add('splide__slide'));

        let type = 'slide';
        let drag = true;
        let rewind = true;
        if (this.cfg.sliderType === 'fade') {
          type = 'fade';
        } else if (this.cfg.nextSlidePreview) {
          // The preview needs loop mode so cloned slides fill the peek.
          type = 'loop';
          rewind = false;
          if (this.cfg.sliderType === 'free') drag = 'free';
        } else if (this.cfg.sliderType === 'free') {
          drag = 'free';
        }
        const gap = this.cfg.sliderType === 'fade' ? 0 : (int(this.cfg.slideGap, 16) || 16) + 'px';
        const edge = !!this.cfg.edgeToEdge && this.cfg.sliderType !== 'fade';
        let padding = 0;
        if (edge) {
          // The stylesheet pulls the track out by --up-edge; hand the same inset straight back
          // so cards keep their size and position and the neighbour arrives inside the clip.
          if (!this.cart) this.trackViewportWidth();
          padding = { left: 'var(--up-edge, 0px)', right: 'var(--up-edge, 0px)' };
        } else if (this.cfg.nextSlidePreview && this.cfg.sliderType !== 'fade') {
          padding = { right: '15%' };
        }

        this.splide = new Splide(splideEl, {
          type,
          rewind,
          drag,
          autoWidth: false,
          snap: this.cfg.sliderType === 'snap',
          perPage: 1,
          perMove: 1,
          gap,
          padding,
          pagination: false,
          arrows: false,
        });
        this.splide.mount();
        if (window.SugarSliderChrome) {
          this.chrome = window.SugarSliderChrome.attach(this.splide, this.root, { dotMode: 'slide' });
        }
        if (this.cart) this.watchSliderSize(splideEl);
      };
      clearTimeout(this.splideTimer);
      if (window.SugarVendor && typeof window.SugarVendor.loadSplide === 'function') {
        window.SugarVendor.loadSplide().then(attempt, attempt);
      } else {
        attempt();
      }
    }

    // A slider mounted while hidden (the drawer closed, or the block waiting on its Show when
    // check) is measured with no layout, so a looping track (Next slide preview) opens on a
    // clone. Re-measure whenever the slider goes from hidden to shown or changes width.
    watchSliderSize(splideEl) {
      if (typeof ResizeObserver !== 'function') return;
      if (this.sizeWatcher) this.sizeWatcher.disconnect();
      let lastWidth = splideEl.offsetWidth;
      this.sizeWatcher = new ResizeObserver(() => {
        const width = splideEl.offsetWidth;
        if (width > 0 && width !== lastWidth && this.splide) {
          try { this.splide.refresh(); } catch (e) { /* unmounted mid-resize */ }
        }
        lastWidth = width;
      });
      this.sizeWatcher.observe(splideEl);
    }

    unmountSlider() {
      clearTimeout(this.splideTimer);
      if (this.sizeWatcher) {
        this.sizeWatcher.disconnect();
        this.sizeWatcher = null;
      }
      if (this.chrome) {
        try { this.chrome.detach(); } catch (e) { /* already gone */ }
        this.chrome = null;
      }
      if (this.splide) {
        try { this.splide.destroy(); } catch (e) { /* already gone */ }
        this.splide = null;
      }
    }

    // 100vw counts the scrollbar and the layout viewport does not, so a bleed sized in vw
    // overhangs by the scrollbar's width. Publish the layout width for the stylesheet instead.
    trackViewportWidth() {
      if (this.vwBound) return;
      const publish = () => this.root.style.setProperty('--upsell-vw', document.documentElement.clientWidth + 'px');
      publish();
      this.vwBound = true;
      this.listen(window, 'resize', publish);
      if (typeof ResizeObserver === 'function') {
        this.vwObserver = new ResizeObserver(publish);
        this.vwObserver.observe(document.documentElement);
      }
    }

    // Slider only: a hidden slide keeps its place in the track, so an upsell already in the
    // cart leaves the DOM and comes back in its original position when the line is removed.
    applyInCartVisibility() {
      if (!this.order) this.order = Array.from(this.list.children);
      this.order = this.order.filter((node) => node.isConnected || node.matches(ROW) || node.querySelector(ROW));
      this.order.forEach((node, i) => {
        const row = node.matches(ROW) ? node : node.querySelector(ROW);
        const hide = row && this.isSelected(row);
        if (hide) {
          if (node.parentNode === this.list) this.list.removeChild(node);
          return;
        }
        if (node.parentNode === this.list) return;
        const next = this.order.slice(i + 1).find((n) => n.parentNode === this.list);
        this.list.insertBefore(node, next || null);
      });
    }

    goToBlock(blockEl) {
      if (!this.splide) return;
      const slides = Array.from(this.list.children);
      const index = slides.findIndex((node) => node === blockEl || node.contains(blockEl));
      if (index > -1) this.splide.go(index);
    }

    /* ------------------------------------------------------------ analytics */

    kind() {
      return this.cart ? 'upsell_cart' : 'upsell_product';
    }

    context() {
      const d = this.root.dataset;
      return {
        template: d.sugarTemplate || '',
        path: window.location.pathname,
        product: d.pageProductHandle || '',
        productTitle: d.sugarProductTitle || '',
        label: '',
        section: d.sectionId || '',
      };
    }

    track(type, detail) {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return;
      const args = [type, this.kind(), entity, String(detail || ''), this.context()];
      if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
      else (window._sugarQ = window._sugarQ || []).push(args);
    }

    attribution() {
      const entity = this.root.dataset.sugarEntity;
      return entity ? entity + ':' + this.kind() : '';
    }

    markSeen() {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return true;
      if (this.visible === false) return false;
      let first = true;
      try {
        if (sessionStorage.getItem('_sugar_imp_' + entity)) first = false;
        else sessionStorage.setItem('_sugar_imp_' + entity, '1');
      } catch (e) { /* storage blocked */ }
      if (first) this.track('impression', '');
      return true;
    }

    setupAnalytics() {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return;
      const drawer = this.root.closest('sugar-cart-drawer');
      if (!drawer) {
        const ctx = this.context();
        const observe = () => window.SugarAnalytics.observe(this.root, entity, this.kind(), ctx);
        if (window.SugarAnalytics) observe();
        else document.addEventListener('sugar:analytics:ready', observe, { once: true });
        return;
      }
      try {
        if (sessionStorage.getItem('_sugar_imp_' + entity)) return;
      } catch (e) { /* storage blocked */ }
      if (drawer.classList.contains('active') && this.markSeen()) return;
      this.drawerWatcher = new MutationObserver(() => {
        if (drawer.classList.contains('active') && this.markSeen()) this.drawerWatcher.disconnect();
      });
      this.drawerWatcher.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ------------------------------------------------------------ lifecycle */

  function init(root) {
    if (!root.sugarUpsells) new Upsells(root);
    return root.sugarUpsells;
  }

  function initAll(scope) {
    const host = scope && scope.querySelectorAll ? scope : document;
    host.querySelectorAll(ROOT).forEach(init);
  }

  function destroyAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(ROOT).forEach((root) => root.sugarUpsells && root.sugarUpsells.destroy());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target && e.target.closest ? e.target.closest(ROOT) : null;
    if (root && root.sugarUpsells) root.sugarUpsells.goToBlock(e.target);
  });

  // The cart page swaps its sections' markup on every cart change; pick up fresh roots and
  // release the ones that left.
  const watcher = new MutationObserver((records) => {
    records.forEach((record) => {
      record.removedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches(ROOT) && node.sugarUpsells) node.sugarUpsells.destroy();
        else destroyAll(node);
      });
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches(ROOT)) init(node);
        else initAll(node);
      });
    });
  });
  const startWatching = () => watcher.observe(document.body, { childList: true, subtree: true });
  if (document.body) startWatching();
  else document.addEventListener('DOMContentLoaded', startWatching);

  window.SugarUpsells = { init, initAll };
})();
